import React, { useMemo } from 'react';
import type { AxiosInstance } from 'axios';
import { useShallow } from 'zustand/react/shallow';
import { Snackbar } from '@common-ui-library/atom';
import ApiService from './UMUtils/apis';
import { UserManagementService } from './Services/UserManagementService';
import { UserManagementContext, UserManagementSession } from './UserManagementContext';
import { useSnackbarStore } from './Store/useSnackbarStore';

interface UserManagementProviderProps {
  children: React.ReactNode;
  client: AxiosInstance;
  session: UserManagementSession | null;
}

export const UserManagementProvider = ({
  children,
  client,
  session,
}: UserManagementProviderProps) => {
  const service = useMemo(() => {
    const api = new ApiService(client);
    return new UserManagementService(api);
  }, [client]);

  const value = useMemo(() => ({ service, session }), [service, session]);
  const { open, message, severity, hideSnackbar } = useSnackbarStore(
    useShallow((state) => ({
      open: state.open,
      message: state.message,
      severity: state.severity,
      hideSnackbar: state.hideSnackbar,
    }))
  );

  return (
    <UserManagementContext.Provider value={value}>
      {children}
      <Snackbar 
        open={open} 
        message={message} 
        severity={severity} 
        onClose={hideSnackbar} 
        autoHideDuration={3000}
      />
    </UserManagementContext.Provider>
  );
};
